import { StyleSheet, Text } from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { useProfileNames } from "../primitives/useProfileNames";

// PE-48 library-coach follow-up — a program/plan that landed in MY
// partition via the assignment fan-out, authored by someone else. Split out
// of LibraryItemCard (405-line file, soft-capped at 350) rather than inlined
// so that card stays layout+grouping.
//
// Name resolution is the shared useProfileNames primitive (GET
// /api/profiles/:sub, batched — here over a single-element array), the SAME
// one ComposerAssigneesBand.tsx calls, never a second mechanism. Its gate
// (resolveCoachReadTarget → isActiveEdge) is symmetric by construction:
// coachAssignment.writeEdgeBothSides mirrors the identical {coachSub,
// athleteSub, status} payload into BOTH partitions at accept time
// (coachEdgeWrites.ts:89-98), so an athlete reading their coach's row hits
// the athlete-side mirror and passes the same active-edge check a coach
// reading an athlete's row would. Verified by reading
// apps/api/src/coachAssignment.ts, lib/coachReadTarget.ts and
// lib/coachEdgeWrites.ts — not assumed.
//
// Fails soft and silent: no badge while unresolved or on a 404/network
// error. The card (and the coach tab's real content) is the point; a name
// that doesn't load yet must never block or blank it.
// ⭐ UX audit fix (2026-09-21): that silent-fail rule is for the coach
// story only. A row the caller merely FOLLOWS reuses this same coachSub
// (isReadOnlyItem, its createdBy), and there `readOnly` says so below: the
// card's own "Stop following" button already names the gesture, so the
// badge must never say "Assigned by" here, and must never go silent
// either, since the missing pencil/trash would then have no explanation
// left at all.
export function LibraryCoachBadge({
  theme,
  apiFetch,
  coachSub,
  presetName,
  readOnly = false,
}: {
  theme: UiTheme;
  apiFetch: ProgramApiFetch;
  coachSub: string;
  /** PE-48 review fix #7 — when the host (LibraryBrowser, via
   *  useAssignedByCoachNames) has ALREADY resolved this coach's name in
   *  ONE batched call over every distinct assignedByCoachSub on screen, it
   *  hands the answer straight through here instead of letting every card
   *  re-run this component's own useProfileNames — which is exactly the
   *  per-card duplicate read this fix removes. `undefined` (the default,
   *  and what the existing unit test below exercises) keeps this
   *  component fully self-sufficient, resolving its own name exactly as
   *  before; `null` means "the batch hasn't resolved it (yet or ever)" and
   *  is deliberately NOT retried here — see the hook's own header. */
  presetName?: string | null;
  /** LibraryItemCard's own readOnly (isReadOnlyItem via followedKeys).
   *  `false` (every existing caller) keeps the coach-assignment copy and
   *  its silent-null fallback exactly as before. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  // A defined `presetName` means some caller already ran the batch this
  // sub belongs to — fetching again here would be the exact duplicate
  // read this fix exists to remove, so subs is [] instead of [coachSub].
  const names = useProfileNames(
    apiFetch,
    presetName !== undefined ? [] : [coachSub],
  );
  const name = presetName !== undefined ? presetName : names[coachSub] ?? null;

  if (readOnly) {
    return (
      <Text style={makeStyles(theme).badge}>
        {name
          ? t("library.followingVersion", {
              name,
              defaultValue: "You're following {{name}}'s version",
            })
          : t("library.followingVersionUnknown", {
              defaultValue: "You're following someone else's version",
            })}
      </Text>
    );
  }

  if (!name) return null;

  return (
    <Text style={makeStyles(theme).badge}>
      {t("library.assignedBy", {
        name,
        defaultValue: "Assigned by {{name}}",
      })}
    </Text>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: t } = theme;
  return StyleSheet.create({
    badge: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
  });
}
